import { FC, PropsWithChildren, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { ReactComponent as LockIcon } from '@/assets/icons/lock.svg';
import Text from '@/components/Text';
import { Permission, Role } from '@/types/resources';
import { RootState } from '@/utils/store';

const StyledLockIcon = styled(LockIcon)`
  cursor: pointer;
  outline: none !important;
  cursor: help;
`;

const Wrapper = styled.div`
  position: relative;
`;

const Tooltip = styled.div`
  position: absolute;
  left: -8px;
  top: 28px;
  width: 136px;
  z-index: 1;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 12px 26px rgba(34, 34, 34, 0.08);
  border-radius: 4px;
  padding: 8px 12px;
`;

const Triangle = styled.div`
  width: 24px;
  height: 10px;
  position: absolute;
  overflow: hidden;
  top: -10px;
  left: 5px;
  &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    background: ${({ theme }) => theme.colors.white};
    transform: rotate(45deg);
    top: 6px;
    left: 5px;
    box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  }
`;

interface Props {
  hideForAll?: boolean;
  role?: Role;
  permission?: Permission;
}

const RestrictedContent: FC<PropsWithChildren<Props>> = ({
  children,
  hideForAll,
  role,
  permission,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const user = useSelector((state: RootState) => state.auth.user!);

  const hasAccess = (() => {
    if (hideForAll) return false;
    if (role) return user.role.name === role;
    if (permission) {
      return !!user.permissions?.find((p) => p === permission);
    }
    return user.role.name === 'Super Admin';
  })();

  if (hasAccess) {
    return <>{children}</>;
  }

  return (
    <Wrapper>
      <StyledLockIcon
        onMouseOver={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      />
      {showTooltip && (
        <Tooltip>
          <Text $type="extraSmall" as="span">
            {!!hideForAll
              ? 'You are not allowed to view this'
              : ' View access forbidden'}
          </Text>
          <Triangle />
        </Tooltip>
      )}
    </Wrapper>
  );
};

export default RestrictedContent;
